import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import numbro from 'numbro';
import { Subscription } from 'rxjs';

import { ChoiceDialog, DialogService } from '../../dialogs';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { Container } from '../../shared/models/container.model';
import { TranslationBundle, TranslationService } from '../../translation.service';
import { FileStatus } from '../upload.model';
import { UploadService } from '../upload.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FwAlertModule } from '@flywheel-io/vision';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { UploadDropzoneComponent } from '../upload-dropzone/upload-dropzone.component';

@Component({
  host: {
    class: 'mat-dialog-component',
  },
  selector: 'upload-dialog',
  styleUrls: ['../upload.shared.scss'],
  templateUrl: './upload-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FwAlertModule,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    UploadDropzoneComponent,
  ],
})
export class UploadDialogComponent {
  containerId = this.data.containerId
  containerFiles = new Set<string>()
  uploadStatus: FileStatus[] = []
  upload = new Subscription() //kept separate from subscriptions object so it can continue uploading after the dialog closes

  private messages = TranslationBundle.EMPTY
  private subscriptions = {
    messages: Subscription.EMPTY,
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: { containerId: string },
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private ref: MatDialogRef<UploadDialogComponent>,
    private uploadService: UploadService,
    private translate: TranslationService
  ) {
    this.coreApi.containers.get<Container>(this.containerId).subscribe(container => {
      this.containerFiles = new Set(container.files.map(file => file.name));
    });
    this.subscriptions.messages = this.translate.getBundle('uploadDialog').subscribe(messages => this.messages = messages);
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  close(): void {
    const pendingUploads = this.uploadStatus.filter(status => status.progress < 100 && !status.error);
    if (pendingUploads.length > 0) {
      this.dialog.open(ChoiceDialog, {
        data: {
          content: this.translate.interpolate(this.messages.get('labels.confirmationOnClose'), pendingUploads.length),
          choices: [
            { label: this.messages.get('labels.cancelUploads'), value: 'cancel' },
            { label: this.messages.get('labels.continueUploads'), value: 'continue', color: 'primary' },
          ],
        },
      }).afterClosed().subscribe(choice => {
        if (choice === 'cancel') {
          this.upload.unsubscribe();
        }
        this.ref.close();
      });
    } else {
      this.ref.close();
    }
  }

  retryFile(status: FileStatus): void {
    status.error = null;
    status.progress = 0;
    this.uploadStatus = this.uploadStatus.filter((other) => {
      // remove any competing duplicates from the list
      if (other.file.name === status.file.name && other !== status) {
        other.subscription.unsubscribe();
        return false;
      }
      return true;
    });
    this.uploadFile(status);
  }

  uploadFileList(files: FileList | File[]): void {
    for (const file of Array.from(files)) {
      const filesize = numbro(file.size ?? 0).format({
        output: 'byte',
        base: 'decimal',
        mantissa: 1,
        spaceSeparated: true,
      }).split(' ') as [string, string];
      const status: FileStatus = { file, size: filesize, progress: 0, subscription: Subscription.EMPTY };
      this.uploadStatus.push(status);
      if (this.containerFiles.has(file.name)) {
        status.duplicate = true;
        status.error = this.messages.get('labels.duplicationError');
      } else {
        this.containerFiles.add(file.name);
        this.uploadFile(status);
      }
    }
  }

  private uploadFile(status: FileStatus): void {
    status.subscription.unsubscribe();
    status.subscription = this.uploadService.uploadFile(status, this.containerId).subscribe({
      next: progress => {
        status.progress = progress;
      },
      error: error => {
        this.notifications.error('container.file.upload.error');
        status.error = error.error?.message || 'Unable to upload file.';
        this.logger.error(error);
      },
      complete: () => {
        this.notifications.success('container.file.upload.success');
      },
    });
    this.upload.add(status.subscription);
  }
}
